import { Typography } from '@euler/app/components/typography';
import { ImportQuestion } from '@euler/app/flows/apdm/imports/components/import-question';
import { importStageLabel } from '@euler/app/flows/apdm/imports/functions/importStageLabel';
import { skipReasonLabel } from '@euler/app/flows/apdm/imports/functions/skipReasonLabel';
import {
  ApdmImportState,
  unanswerableGaps,
} from '@euler/functions/apdm/import/importState';
import {
  ApdmImportAction,
  useApdmImport,
} from '@euler/functions/apdm/import/useApdmImport';
import { canManageApdmImports } from '@euler/functions/apdmAccess';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useUserProfileInfo } from '@euler/functions/user/useUserProfileInfo';
import { ApdmBuildStep } from '@euler/model/apdm';
import { parseApdmImportMappingWidgetPayload } from '@euler/model/apdm/widgets';
import { ChevronDown, FileCheck, FileSpreadsheet } from 'lucide-react-native';
import { memo, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import {
  AiChatPlanContext,
  useAiChatActions,
  useAiChatMessage,
} from '../chat/AiChatActions';
import {
  CardLoading,
  CardNotice,
  ChatCard,
  ChatCardBadge,
  StatTile,
} from '../components/ChatCard';
import { PrimaryButton } from '../components/PrimaryButton';
import { ImportDetailPanel } from '../import-detail/ImportDetailPanel';
import { aiColors } from '../theme';

/** The sheet the assistant is importing: progress, question, summary. */
export const ImportChatCard = memo(
  ({ data, streaming }: { data: unknown; streaming?: boolean }) => {
    const profile = useUserProfileInfo();
    const ref = parseApdmImportMappingWidgetPayload(data);
    if (streaming) return null;
    if (!ref) return <CardNotice text="这次导入的引用无效，请重新上传表格。" />;
    if (!canManageApdmImports(profile ?? null))
      return <CardNotice text="当前账号无法查看这次导入。" />;
    return (
      <ImportCardBody
        key={`${profile!.store.id}:${ref.importId}`}
        importId={ref.importId}
      />
    );
  },
);

function ImportCardBody({ importId }: { importId: string }) {
  const { rpx } = useResponsiveContext();
  const controller = useApdmImport(importId);
  const { state, pending, failure, actions } = controller;
  const chat = useAiChatActions();
  const { latest } = useAiChatMessage();
  const audienceOpen =
    useContext(AiChatPlanContext)?.currentStep === ApdmBuildStep.Who;
  // The build goes on once the sheet is in: the planner counts the
  // audience again with it. The app says so itself, without the user.
  const continueBuild = () =>
    chat.send('客户表格导入好了', {
      answer: { step: ApdmBuildStep.Who, importId },
    });
  const seen = useRef(state?.kind);
  useEffect(() => {
    const before = seen.current;
    seen.current = state?.kind;
    if (before === 'publishing' && state?.kind === 'published' && audienceOpen)
      continueBuild();
    // Only the transition seen here continues; a card opened later offers
    // the button instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.kind]);
  const [why, setWhy] = useState(false);
  const [opened, setOpened] = useState(false);
  const detail = { label: '查看导入详情', onPress: () => setOpened(true) };
  const panel = (
    <ImportDetailPanel
      visible={opened}
      controller={controller}
      onClose={() => setOpened(false)}
    />
  );
  if (!state)
    return (
      <ChatCard icon={FileSpreadsheet} color={aiColors.green} title="客户表格">
        {failure ? (
          <CardNotice tone="danger" text={failure} />
        ) : (
          <CardLoading label="正在读取导入…" />
        )}
      </ChatCard>
    );
  const summary =
    state.kind === 'ready' ||
    state.kind === 'publishing' ||
    state.kind === 'published' ||
    state.kind === 'failed'
      ? state.summary
      : null;
  return (
    <>
      <ChatCard
        icon={state.kind === 'published' ? FileCheck : FileSpreadsheet}
        color={aiColors.green}
        title={
          state.kind === 'question' && unanswerableGaps(state.question).length
            ? '表格缺少需要的内容'
            : kTitles[state.kind]
        }
        subtitle={state.fileName}
        badge={badge(state)}
        detail={state.kind === 'deleted' ? null : detail}
      >
        {state.kind === 'working' && (
          <CardLoading label={`${importStageLabel(state.stage)}…`} />
        )}
        {state.kind === 'question' && (
          <ImportQuestion
            key={state.id}
            question={state.question}
            busy={pending === ApdmImportAction.Answer}
            onAnswer={answers => void actions.answer(answers)}
          />
        )}
        {summary && (
          <>
            <View style={{ flexDirection: 'row', gap: rpx(8) }}>
              <StatTile value={summary.leftOut.totalRows} label="读取行数" />
              {!summary.contactList && (
                <StatTile value={summary.visits} label="到店记录" />
              )}
              <StatTile value={summary.vehicles} label="车辆" />
              <StatTile
                value={summary.leftOut.rows}
                label="未导入"
                tone={summary.leftOut.rows ? 'danger' : undefined}
              />
            </View>
            {summary.leftOut.byReason.length > 0 && (
              <View
                style={{
                  borderWidth: 1,
                  borderColor: aiColors.border,
                  borderRadius: rpx(10),
                }}
              >
                <Pressable
                  accessibilityRole="button"
                  accessibilityState={{ expanded: why }}
                  onPress={() => setWhy(value => !value)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingHorizontal: rpx(12),
                    paddingVertical: rpx(9),
                  }}
                >
                  <Typography.Text
                    medium
                    size={rpx(12)}
                    color={aiColors.foreground}
                  >
                    为什么有 {summary.leftOut.rows} 行未导入
                  </Typography.Text>
                  <ChevronDown
                    size={14}
                    color={aiColors.mutedForeground}
                    style={{ transform: [{ rotate: why ? '180deg' : '0deg' }] }}
                  />
                </Pressable>
                {why && (
                  <View
                    style={{
                      borderTopWidth: 1,
                      borderTopColor: aiColors.border,
                      paddingHorizontal: rpx(12),
                      paddingVertical: rpx(8),
                      gap: rpx(6),
                    }}
                  >
                    {summary.leftOut.byReason.map(entry => (
                      <View
                        key={entry.reason}
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography.Text
                          size={rpx(12)}
                          color={aiColors.mutedForeground}
                        >
                          {skipReasonLabel(entry.reason)}
                        </Typography.Text>
                        <Typography.Text
                          medium
                          size={rpx(12)}
                          color={aiColors.foreground}
                        >
                          {entry.rows}
                        </Typography.Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            )}
          </>
        )}
        {state.kind === 'publishing' && (
          <CardLoading label="正在导入，客户会陆续出现在营销名单里…" />
        )}
        {state.kind === 'published' && audienceOpen && latest && (
          <PrimaryButton
            title="继续"
            disabled={chat.busy}
            onPress={() => void continueBuild()}
          />
        )}
        {state.kind === 'failed' && (
          <CardNotice
            tone="danger"
            text={
              state.restartable
                ? '导入中途停止了，可以重新开始。'
                : '这份文件没能导入，请换一份文件再试。'
            }
          />
        )}
        {failure && <CardNotice tone="danger" text={failure} />}
        {(state.kind === 'ready' ||
          (state.kind === 'failed' && state.restartable)) && (
          <PrimaryButton
            title={state.kind === 'ready' ? '开始导入' : '重新开始导入'}
            loading={pending === ApdmImportAction.Start}
            onPress={() => void actions.start()}
          />
        )}
      </ChatCard>
      {panel}
    </>
  );
}

const kTitles: Record<ApdmImportState['kind'], string> = {
  working: '正在读取客户表格',
  question: '需要您指出表格里的列',
  ready: '客户表格已整理好',
  publishing: '正在导入客户表格',
  published: '客户表格已导入',
  failed: '客户表格导入未完成',
  deleted: '这次导入已删除',
};

function badge(state: ApdmImportState) {
  switch (state.kind) {
    case 'ready':
      return <ChatCardBadge label="待开始" color={aiColors.amber} />;
    case 'published':
      return <ChatCardBadge label="已完成" color={aiColors.green} />;
    case 'failed':
      return <ChatCardBadge label="未完成" color={aiColors.destructive} />;
    default:
      return null;
  }
}
